import { useState, useEffect, useCallback } from "react";

interface LikeButtonProps {
  articleId: string;
  initialLikeCount: number;
  size?: "sm" | "md";
}

function formatCount(n: number): string {
  if (n >= 10000) {
    return `${(n / 10000).toFixed(1)}万`;
  }
  return String(n);
}

export default function LikeButton({ articleId, initialLikeCount, size = "md" }: LikeButtonProps) {
  const storageKey = `article_liked_${articleId}`;
  const countKey = `article_like_count_${articleId}`;

  const [liked, setLiked] = useState(false);
  const [localCount, setLocalCount] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    setLiked(stored === "true");

    const storedCount = localStorage.getItem(countKey);
    if (storedCount !== null) {
      setLocalCount(parseInt(storedCount, 10));
    }
  }, [storageKey, countKey]);

  const displayCount = localCount !== null ? localCount : initialLikeCount;

  const toggleLike = useCallback(() => {
    const newLiked = !liked;
    setLiked(newLiked);
    setAnimating(true);
    setTimeout(() => setAnimating(false), 400);

    localStorage.setItem(storageKey, String(newLiked));

    const delta = newLiked ? 1 : -1;
    const newCount = displayCount + delta;
    setLocalCount(newCount);
    localStorage.setItem(countKey, String(newCount));
  }, [liked, displayCount, storageKey, countKey]);

  const isSm = size === "sm";

  return (
    <button
      onClick={toggleLike}
      className={`inline-flex items-center gap-1.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap select-none ${
        liked
          ? "bg-accent-100 text-accent-600 hover:bg-accent-200"
          : "bg-background-100 text-foreground-500 hover:bg-background-200"
      } ${isSm ? "px-2.5 py-1 text-xs" : "px-4 py-2 text-sm font-medium"}`}
      aria-label={liked ? "取消点赞" : "点赞"}
      title={liked ? "取消点赞" : "点赞"}
    >
      <span
        className={`${isSm ? "w-3.5 h-3.5" : "w-4 h-4"} flex items-center justify-center transition-transform duration-300 ${
          animating ? "scale-125" : "scale-100"
        }`}
      >
        <i className={`${liked ? "ri-heart-fill text-accent-500" : "ri-heart-line"} ${isSm ? "text-xs" : "text-sm"}`}></i>
      </span>
      <span>{formatCount(displayCount)}</span>
    </button>
  );
}